/**
 * Centralized logging utility for EZAccounting
 * Provides consistent, configurable logging across the application
 */

import { getLogLevel } from '../config/logging';

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

class Logger {
  private level: LogLevel;
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.level = getLogLevel();
  }

  setLevel(level: LogLevel) {
    this.level = level;
  }

  private shouldLog(level: LogLevel): boolean {
    return level <= this.level;
  }

  error(message: string, ...args: any[]) {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(`❌ ${message}`, ...args);
    }
  }

  warn(message: string, ...args: any[]) {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(`⚠️ ${message}`, ...args);
    }
  }

  info(message: string, ...args: any[]) {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(`ℹ️ ${message}`, ...args);
    }
  }

  debug(message: string, ...args: any[]) {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`🐛 ${message}`, ...args);
    }
  }

  // Voice-specific logging (reduced verbosity)
  voiceStart() {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log('🎤 Voice recognition started');
    }
  }

  voiceEnd() {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log('🎤 Voice recognition ended');
    }
  }

  voiceTranscription(text: string) {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log('📝 Voice:', text);
    }
  }

  voiceProcessing() {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log('🔄 Processing voice command...');
    }
  }

  voiceResponse(response: any) {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log('✅ Voice response:', response.message || 'Command processed');
    }
  }

  // API logging (reduced verbosity)
  apiCall(endpoint: string, method: string = 'GET') {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`🌐 API ${method}: ${endpoint}`);
    }
  }

  apiResponse(endpoint: string, status: number) {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`🌐 API Response: ${endpoint} - ${status}`);
    }
  }

  // Navigation logging (reduced verbosity)
  navigation(from: string, to: string) {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`🧭 Navigation: ${from} → ${to}`);
    }
  }

  // Translation logging (reduced verbosity)
  translation(key: string, language: string) {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`🌍 Translation: ${key} (${language})`);
    }
  }

  // Component logging (reduced verbosity)
  componentMount(component: string) {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`📦 Component mounted: ${component}`);
    }
  }

  componentUnmount(component: string) {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`📦 Component unmounted: ${component}`);
    }
  }
}

// Create singleton instance
const logger = new Logger();

// Export for use in components
export default logger;

// Export convenience methods
export const log = {
  error: logger.error.bind(logger),
  warn: logger.warn.bind(logger),
  info: logger.info.bind(logger),
  debug: logger.debug.bind(logger),
  voice: {
    start: logger.voiceStart.bind(logger),
    end: logger.voiceEnd.bind(logger),
    transcription: logger.voiceTranscription.bind(logger),
    processing: logger.voiceProcessing.bind(logger),
    response: logger.voiceResponse.bind(logger)
  },
  api: {
    call: logger.apiCall.bind(logger),
    response: logger.apiResponse.bind(logger)
  },
  navigation: logger.navigation.bind(logger),
  translation: logger.translation.bind(logger),
  component: {
    mount: logger.componentMount.bind(logger),
    unmount: logger.componentUnmount.bind(logger)
  }
};
